import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';

import { SubNavComponent } from './components/sub-nav/sub-nav.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { StandardPageComponent } from './components/standard-page/standard-page.component';
import { NavigationComponent } from './pages/navigation/navigation.component';
import { PrimeModule } from '../prime.module';
import { TooltipModule } from 'primeng/tooltip';
import { HasRoleDirective } from './_directives/has-role.directive';
import { WebSocketAccessDirective } from './_directives/websocket-access.directive';
import { RoleContextMenuDirective } from './_directives/role-context-menu.directive';
import { HasPermissionDirective } from './_directives/has-permission.directive';
import { LoginComponent } from './pages/login/login.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { ForgotUsernameComponent } from './components/forgot-username/forgot-username.component';
import { DateAsAgoPipe } from './_pipes/date-as-ago.pipe';
import { BoolAsStringPipe } from './_pipes/bool-as-string.pipe';
import { FilesizePipe } from './_pipes/filesize.pipe';
import { NestedKeyvaluePipe } from './_pipes/nested-keyvalue.pipe';
import { TruncateFilenamePipe } from './_pipes/truncate-filename.pipe';
import { ShareSubscriptionDetailsComponent } from './components/share-subscription-details/share-subscription-details.component';
import { ShareNetworkStatusComponent } from './components/share-network-status/share-network-status.component';
import { RemoveSPipe } from './_pipes/remove-s.pipe';
import { ShareUserCustomerDetailsComponent } from './components/share-user-customer-details/share-user-customer-details.component';
import { ShareFormComponent } from './components/share-form/share-form.component';
import { AnyAsStatusStringPipe } from './_pipes/any-as-status-string';
import { HumanizePipe } from './_pipes/humanize.pipe';
import { TicketMessageAuthorPipe } from './_pipes/ticket-message-author.pipe';
import { ShareUserFilesComponent } from './components/share-user-files/share-user-files.component';
import { ShareSubscriptionActionBarComponent } from './components/share-subscription-action-bar/share-subscription-action-bar.component';
import { ShareTableComponent } from './components/share-table/share-table.component';
import { ShareTransactionsTableComponent } from './components/share-transactions-table/share-transactions-table.component';
import { ShareFilterComponent } from './components/share-filter/share-filter.component';
import { SharePaymentDialogComponent } from './components/share-payment-dialog/share-payment-dialog.component';
import { ChartWrapperComponent } from './components/chart-wrapper/chart-wrapper.component';
import { NotificationBellComponent } from './components/notification-bell/notification-bell.component';

import { ShareCustomerActionBarComponent } from './components/share-customer-action-bar/share-customer-action-bar.component';
import { ShareDataTableComponent } from './components/share-data-table/share-data-table.component';
import { PasswordResetComponent } from './pages/password-reset/password-reset.component';
import { UserPreferencesComponent } from './components/user-preferences/user-preferences.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { ShareGeoMapComponent } from './components/share-geo-map/share-geo-map.component';
import { ShareEndCustomerCircuitDetailsComponent } from './components/share-end-customer-circuit-details/share-end-customer-circuit-details.component';
import { ShareAuditLogsComponent } from './components/share-audit-logs/share-audit-logs.component';
import { ShareCircuitActionBarComponent } from './components/share-circuit-action-bar/share-circuit-action-bar.component';
import { ShareAccountDetailsComponent } from './components/share-account-details/share-account-details.component';
import { SharePageHeaderComponent } from './components/share-page-header/share-page-header.component';
import { ShareStatCardComponent } from './components/share-stat-card/share-stat-card.component';
import { ShareSectionCardComponent } from './components/share-section-card/share-section-card.component';
import { ShareModuleTabsComponent } from './components/share-module-tabs/share-module-tabs.component';
import { ShareEntityDetailPanelComponent } from './components/share-entity-detail-panel/share-entity-detail-panel.component';
import { ShareEmptyStateComponent } from './components/share-empty-state/share-empty-state.component';
import { SharePortalBillingSettingsComponent } from './components/share-portal-billing-settings/share-portal-billing-settings.component';
import { SharePortalSafetyFlagsComponent } from './components/share-portal-safety-flags/share-portal-safety-flags.component';
import { SendCommunicationDialogComponent } from './components/send-communication-dialog/send-communication-dialog.component';

@NgModule({
  declarations: [
    NavigationComponent,
    SubNavComponent,
    SidebarComponent,
    StandardPageComponent,

    LoginComponent,
    ForgotPasswordComponent,
    ForgotUsernameComponent,

    // directives
    HasRoleDirective,
    WebSocketAccessDirective,
    RoleContextMenuDirective,
    HasPermissionDirective,

    // pipes
    DateAsAgoPipe,
    BoolAsStringPipe,
    FilesizePipe,
    NestedKeyvaluePipe,
    TruncateFilenamePipe,
    RemoveSPipe,
    AnyAsStatusStringPipe,
    HumanizePipe,
    TicketMessageAuthorPipe,

    // components
    ShareSubscriptionDetailsComponent,
    ShareNetworkStatusComponent,
    ShareUserCustomerDetailsComponent,
    ShareFormComponent,
    ShareUserFilesComponent,
    ShareSubscriptionActionBarComponent,
    ShareTableComponent,
    ShareTransactionsTableComponent,
    ShareFilterComponent,
    SharePaymentDialogComponent,
    ShareCustomerActionBarComponent,
    ChartWrapperComponent,
    NotificationBellComponent,
    ShareDataTableComponent,

    PasswordResetComponent,
    UserPreferencesComponent,
    UserProfileComponent,
    ShareGeoMapComponent,
    ShareEndCustomerCircuitDetailsComponent,
    ShareAuditLogsComponent,
    ShareCircuitActionBarComponent,
    ShareAccountDetailsComponent,
    SharePageHeaderComponent,
    ShareStatCardComponent,
    ShareSectionCardComponent,
    ShareModuleTabsComponent,
    ShareEntityDetailPanelComponent,
    ShareEmptyStateComponent,
    SharePortalBillingSettingsComponent,
    SharePortalSafetyFlagsComponent,
    SendCommunicationDialogComponent
  ],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PrimeModule, RouterModule, TooltipModule],
  exports: [
    NavigationComponent,
    SubNavComponent,
    SidebarComponent,
    StandardPageComponent,
    ChartWrapperComponent,

    SharePaymentDialogComponent,
    SubNavComponent,
    LoginComponent,
    ForgotPasswordComponent,
    ForgotUsernameComponent,
    HasRoleDirective,
    WebSocketAccessDirective,
    RoleContextMenuDirective,
    HasPermissionDirective,
    DateAsAgoPipe,
    BoolAsStringPipe,
    FilesizePipe,
    NestedKeyvaluePipe,
    TruncateFilenamePipe,
    RemoveSPipe,
    AnyAsStatusStringPipe,
    HumanizePipe,
    TicketMessageAuthorPipe,

    // components
    ShareSubscriptionDetailsComponent,
    ShareNetworkStatusComponent,
    ShareUserCustomerDetailsComponent,
    ShareFormComponent,
    ShareUserFilesComponent,
    ShareSubscriptionActionBarComponent,
    ShareTableComponent,
    ShareTransactionsTableComponent,
    ShareFilterComponent,
    SharePaymentDialogComponent,
    ShareCustomerActionBarComponent,
    ShareTableComponent,
    ShareTransactionsTableComponent,
    ChartWrapperComponent,
    ShareTransactionsTableComponent,
    NotificationBellComponent,
    ShareDataTableComponent,
    PasswordResetComponent,
    UserPreferencesComponent,
    UserProfileComponent,
    ShareGeoMapComponent,
    ShareEndCustomerCircuitDetailsComponent,
    ShareAuditLogsComponent,
    ShareCircuitActionBarComponent,
    ShareAccountDetailsComponent,
    SharePageHeaderComponent,
    ShareStatCardComponent,
    ShareSectionCardComponent,
    ShareModuleTabsComponent,
    ShareEntityDetailPanelComponent,
    ShareEmptyStateComponent,
    SharePortalBillingSettingsComponent,
    SharePortalSafetyFlagsComponent,
    TooltipModule,
    SendCommunicationDialogComponent
  ]
})
export class ShareModule {}
